import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  Alert,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';
import DataTable from '../components/DataTable';
import StatCard from '../components/StatCard';
import { useAdminApi } from '../hooks/useAdminApi';
import { BillingOverviewResponse, BillingOrganizationSummary } from '../types/adminApiTypes';
import { formatNumber } from '../utils/helpers';

// Define columns for the DataGrid
const columns: GridColDef<BillingOrganizationSummary>[] = [
  {
    field: 'organization',
    headerName: 'Organization',
    flex: 1,
    renderCell: params => (
      // Link to the organization billing detail view
      <MuiLink component={RouterLink} to={`/dashboard/billing/${encodeURIComponent(params.value)}`}>
        {params.value}
      </MuiLink>
    ),
  },
  {
    field: 'total_costs',
    headerName: 'Total Costs',
    type: 'number',
    width: 140,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => {
      // Format as currency
      const numValue = value as number;
      if (numValue === null || numValue === undefined) return '$0.00';
      return `$${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    field: 'total_openai_costs',
    headerName: 'OpenAI Costs',
    type: 'number',
    width: 140,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => {
      const numValue = value as number;
      if (numValue === null || numValue === undefined) return '$0.00';
      return `$${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    field: 'total_google_costs',
    headerName: 'Google Costs',
    type: 'number',
    width: 140,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => {
      const numValue = value as number;
      if (numValue === null || numValue === undefined) return '$0.00';
      return `$${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    field: 'total_other_llm_costs',
    headerName: 'Other LLM Costs',
    type: 'number',
    width: 150,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => {
      const numValue = value as number;
      if (numValue === null || numValue === undefined) return '$0.00';
      return `$${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    field: 'total_user_costs',
    headerName: 'User Costs',
    type: 'number',
    width: 140,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => {
      const numValue = value as number;
      if (numValue === null || numValue === undefined) return '$0.00';
      return `$${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    field: 'total_api_costs',
    headerName: 'API Costs',
    type: 'number',
    width: 140,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => {
      const numValue = value as number;
      if (numValue === null || numValue === undefined) return '$0.00';
      return `$${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
];

const BillingView: React.FC = () => {
  const { data, loading, error, fetchData } = useAdminApi<BillingOverviewResponse>();
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    fetchData({ endpoint: '/billing' });
  }, [fetchData]);

  // Client-side filtering of organizations based on search term
  const filteredOrganizations = useMemo(() => {
    if (!data?.organizations) return [];
    if (!searchTerm.trim()) return data.organizations;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return data.organizations.filter(org =>
      org.organization.toLowerCase().includes(lowerSearchTerm)
    );
  }, [data?.organizations, searchTerm]);

  if (error) {
    return <Alert severity="error">Error loading billing data: {error}</Alert>;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h5" gutterBottom component="div">
        Billing Overview
      </Typography>

      {/* Summary Tiles Row 1: LLM Provider Costs */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Costs"
            value={data?.summary?.total_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="OpenAI Costs"
            value={data?.summary?.total_openai_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Google Costs"
            value={data?.summary?.total_google_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Other LLM Costs"
            value={data?.summary?.total_other_llm_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Summary Tiles Row 2: Usage-Based Costs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="User Costs"
            value={data?.summary?.total_user_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="API Costs"
            value={data?.summary?.total_api_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Search Bar Section */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ width: { xs: '100%', sm: '300px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Organizations Cost Breakdown Table */}
      <DataTable
        rows={filteredOrganizations}
        columns={columns}
        loading={loading}
        getRowId={row => row.organization} // Use organization name as unique ID
        // Pagination/Sorting are client-side by default for this endpoint
      />
    </Box>
  );
};

export default BillingView;
